import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { ReactComponent as Calender } from '../../assets/icons/calender.svg';
import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/right-arrow.svg';
import { changeDateFormat } from '../../shared-functions/DateTime';

/**
 * Calendar grid properties.
 */
interface CalendarGridProperties {
  days: any[];
  onDayClick: (date: string, fullDate: Date) => void;
  getDayClass: (dateObject: Date | undefined) => string;
  onHover: (date: string | null) => void;
}

/**
 * CalendarGrid component to render day cells for a month.
 * @param {CalendarGridProperties} properties - The component properties.
 * @param {any[]} properties.days - The days array.
 * @param {Function} properties.onDayClick - Handler for day click.
 * @param {Function} properties.getDayClass - Function to get day CSS class.
 * @param {Function} properties.onHover - Handler for hovering over a day.
 * @returns {any} The rendered calendar grid.
 */
function CalendarGrid({ days, onDayClick, getDayClass, onHover }: CalendarGridProperties): JSX.Element {
  return (
    <div className="calender-body">
      <div className="calender-header-items">
        {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
          <div key={d} className="header-item-label">
            {d}
          </div>
        ))}
      </div>
      <div className="calender-body-items">
        {days.map((d: any, index: number) => (
          <div
            key={index}
            role="presentation"
            className={`day-item ${Object.keys(d).length === 0 ? 'empty' : ''} ${d.fullDate ? getDayClass(d.fullDate) : ''}`}
            onClick={() => d.date && onDayClick(d.date, d.fullDate)}
            onMouseEnter={() => d.date && onHover(d.date)}>
            {d.number}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Supported preset keys for default date range selection.
 */
export type DateRangePreset =
  | 'thisMonth'
  | 'oneMonth'
  | 'prevMonth'
  | 'thisWeek'
  | 'last7Days'
  | 'prevWeek'
  | 'prev2Week'
  | 'prev2Weeks'
  | 'last14Days'
  | 'this_month'
  | 'one_month'
  | 'prev_month'
  | 'this_week'
  | 'last_7_days'
  | 'prev_week'
  | 'prev_2_weeks'
  | 'last_14_days'
  | 'custom';

/**
 * CustomDateRangePicker properties interface.
 */
export interface CustomDateRangePickerProperties {
  inputLabel?: string;
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
  placeholder?: string;
  readOnly?: boolean;
  valueRequired?: boolean;
  defaultDuration?: DateRangePreset;
  defaultPreset?: DateRangePreset;
  defaultDurationSelection?: DateRangePreset;
}

/**
 * Normalizes preset names (camelCase, snake_case, spaces) to standard internal key.
 * @param {DateRangePreset} [preset] - The preset value to normalize.
 * @returns {string} The normalized preset key.
 */
export const normalizePresetKey = (preset?: DateRangePreset): string => {
  if (!preset) return 'custom';
  switch (preset) {
    case 'thisMonth':
    case 'this_month':
      return 'this_month';
    case 'oneMonth':
    case 'one_month':
      return 'one_month';
    case 'prevMonth':
    case 'prev_month':
      return 'prev_month';
    case 'thisWeek':
    case 'this_week':
      return 'this_week';
    case 'last7Days':
    case 'last_7_days':
      return 'last_7_days';
    case 'prevWeek':
    case 'prev_week':
      return 'prev_week';
    case 'prev2Week':
    case 'prev2Weeks':
    case 'prev_2_weeks':
      return 'prev_2_weeks';
    case 'last14Days':
    case 'last_14_days':
      return 'last_14_days';
    case 'custom':
    default:
      return 'custom';
  }
};

/**
 * Calculates start and end dates for a given preset key.
 * @param {string} key - The preset key.
 * @returns {{ startDate: string; endDate: string; startDateObj: Date; endDateObj: Date } | null} The calculated date range or null.
 */
export const calculateDateRange = (
  key: string,
): { startDate: string; endDate: string; startDateObj: Date; endDateObj: Date } | null => {
  const today = new Date();
  let start: Date;
  let end: Date;

  switch (key) {
    case 'this_month':
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
      break;
    case 'one_month': // Last 30 days
      start = new Date(today);
      start.setDate(today.getDate() - 30);
      end = new Date(today);
      break;
    case 'prev_month':
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
      break;
    case 'this_week': {
      start = new Date(today);
      start.setDate(today.getDate() - today.getDay());
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      break;
    }
    case 'last_7_days':
      start = new Date(today);
      start.setDate(today.getDate() - 6);
      end = new Date(today);
      break;
    case 'prev_week': {
      const currentSunday = new Date(today);
      currentSunday.setDate(today.getDate() - today.getDay());
      start = new Date(currentSunday);
      start.setDate(currentSunday.getDate() - 7);
      end = new Date(start);
      end.setDate(start.getDate() + 6);
      break;
    }
    case 'prev_2_weeks': {
      const currentSunday = new Date(today);
      currentSunday.setDate(today.getDate() - today.getDay());
      end = new Date(currentSunday);
      end.setDate(currentSunday.getDate() - 1);
      start = new Date(end);
      start.setDate(end.getDate() - 13);
      break;
    }
    case 'last_14_days':
      start = new Date(today);
      start.setDate(today.getDate() - 13);
      end = new Date(today);
      break;
    default:
      return null;
  }

  return {
    startDate: changeDateFormat(start.toString()),
    endDate: changeDateFormat(end.toString()),
    startDateObj: start,
    endDateObj: end,
  };
};

/**
 * List of preset definitions with labels and translation keys.
 */
const presets = [
  { label: 'This month', key: 'this_month', translationKey: 'thisMonth' },
  { label: 'One month', key: 'one_month', translationKey: 'oneMonth' },
  { label: 'Prev month', key: 'prev_month', translationKey: 'prevMonth' },
  { label: 'This week', key: 'this_week', translationKey: 'thisWeek' },
  { label: 'Last 7 days', key: 'last_7_days', translationKey: 'last7Days' },
  { label: 'Prev week', key: 'prev_week', translationKey: 'prevWeek' },
  { label: 'Prev 2 weeks', key: 'prev_2_weeks', translationKey: 'prev2Weeks' },
  { label: 'Last 14 days', key: 'last_14_days', translationKey: 'last14Days' },
  { label: 'Custom Range', key: 'custom', translationKey: 'customRange' },
];

/**
 * List of month names.
 */
const monthsList = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * This function is used to render the custom date range picker.
 * @param {CustomDateRangePickerProperties} properties - The component properties.
 * @param {string} [properties.inputLabel] - The label for the input field.
 * @param {string} properties.startDate - The startDate prop.
 * @param {string} properties.endDate - The endDate prop.
 * @param {Function} properties.onChange - The onChange prop.
 * @param {string} [properties.placeholder] - The placeholder prop.
 * @param {boolean} [properties.readOnly] - The readOnly prop.
 * @param {boolean} [properties.valueRequired] - The valueRequired prop.
 * @param {DateRangePreset} [properties.defaultDuration] - The default duration preset.
 * @param {DateRangePreset} [properties.defaultPreset] - The default preset alias.
 * @param {DateRangePreset} [properties.defaultDurationSelection] - The default duration selection alias.
 * @returns {any} The custom date range picker.
 */
function CustomDateRangePicker(properties: CustomDateRangePickerProperties): JSX.Element {
  const {
    inputLabel,
    startDate,
    endDate,
    onChange,
    placeholder,
    readOnly,
    valueRequired,
    defaultDuration,
    defaultPreset,
    defaultDurationSelection,
  } = properties;

  const reference = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation('customDate');
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState<any>({});

  const chosenDefault = defaultDuration || defaultPreset || defaultDurationSelection;
  const normalizedDefault = normalizePresetKey(chosenDefault);

  // View state for the LEFT calendar
  const [viewMonth, setViewMonth] = useState(new Date().getMonth());
  const [viewYear, setViewYear] = useState(new Date().getFullYear());

  const [activePreset, setActivePreset] = useState<string>(() => {
    if (normalizedDefault !== 'custom') {
      return normalizedDefault;
    }
    return 'custom';
  });

  const [temporaryStartDate, setTemporaryStartDate] = useState(startDate);
  const [temporaryEndDate, setTemporaryEndDate] = useState(endDate);
  const [hoverDate, setHoverDate] = useState<string | null>(null);

  // Calendar Data
  const [leftCalendarDays, setLeftCalendarDays] = useState<any[]>([]);
  const [rightCalendarDays, setRightCalendarDays] = useState<any[]>([]);

  // Initialize view based on current startDate or today
  useEffect(() => {
    if (startDate) {
      const date = new Date(startDate);
      if (!Number.isNaN(date.getTime())) {
        setViewMonth(date.getMonth());
        setViewYear(date.getFullYear());
      }
    }
  }, [startDate, open]);

  // Update temp state when props change
  useEffect(() => {
    setTemporaryStartDate(startDate);
    setTemporaryEndDate(endDate);
  }, [startDate, endDate]);

  // Apply default duration on mount if startDate and endDate are not provided
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (!startDate && !endDate && normalizedDefault !== 'custom') {
        const range = calculateDateRange(normalizedDefault);
        if (range) {
          onChange(range.startDate, range.endDate);
          setTemporaryStartDate(range.startDate);
          setTemporaryEndDate(range.endDate);
          setActivePreset(normalizedDefault);
          setViewMonth(range.startDateObj.getMonth());
          setViewYear(range.startDateObj.getFullYear());
        }
      }
    }
  }, [startDate, endDate, normalizedDefault, onChange]);

  // Handle dynamic changes to default duration prop
  const previousDefaultPreset = useRef(normalizedDefault);
  useEffect(() => {
    if (previousDefaultPreset.current !== normalizedDefault) {
      previousDefaultPreset.current = normalizedDefault;
      if (normalizedDefault !== 'custom') {
        const range = calculateDateRange(normalizedDefault);
        if (range) {
          onChange(range.startDate, range.endDate);
          setTemporaryStartDate(range.startDate);
          setTemporaryEndDate(range.endDate);
          setActivePreset(normalizedDefault);
          setViewMonth(range.startDateObj.getMonth());
          setViewYear(range.startDateObj.getFullYear());
        }
      }
    }
  }, [normalizedDefault, onChange]);

  /**
   * Generates an array of day objects for a given month and year.
   * @param {number} month - The month (0-11).
   * @param {number} year - The year.
   * @returns {any[]} An array of day objects.
   */
  const generateDays = (month: number, year: number) => {
    const date = new Date(year, month, 1);
    const days = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    const formattedDays = days.map((d) => ({
      date: changeDateFormat(d.toString()), // e.g. "YYYY-MM-DD" depending on util
      fullDate: d,
      number: d.getDate(),
      day: d.getDay(),
    }));

    // Fill padding for empty start slots
    if (formattedDays.length > 0 && formattedDays[0].day > 0) {
      const paddingCount = formattedDays[0].day;
      const padding = Array(paddingCount).fill({});
      return [...padding, ...formattedDays];
    }
    return formattedDays;
  };

  useEffect(() => {
    setLeftCalendarDays(generateDays(viewMonth, viewYear));

    // Right calendar is next month
    let rightMonth = viewMonth + 1;
    let rightYear = viewYear;
    if (rightMonth > 11) {
      rightMonth = 0;
      rightYear += 1;
    }
    setRightCalendarDays(generateDays(rightMonth, rightYear));
  }, [viewMonth, viewYear]);

  /**
   * Handles click outside the date picker component.
   * @param {MouseEvent} event - The mouse event.
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  /**
   * Toggles the open state of the date picker.
   * @param {React.MouseEvent<HTMLElement>} event - The mouse event.
   */
  const toggleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (readOnly) return;
    setOpen(!open);
    if (!open) {
      // Calculate position
      let rect;
      const targetElement = event.target as HTMLElement;
      if (targetElement.classList.contains('custom-input-container')) {
        rect = targetElement.getBoundingClientRect();
      } else {
        rect = targetElement.closest('.custom-input-container')?.getBoundingClientRect();
      }

      if (rect) {
        const POPUP_HEIGHT = 450; // Safety max height for decision
        const POPUP_WIDTH = 690;
        const SCREEN_HEIGHT = window.innerHeight;
        const SCREEN_WIDTH = window.innerWidth;

        const styleObject: any = {};
        let left = rect.left + window.scrollX;

        // Horizontal adjustment
        if (rect.left + POPUP_WIDTH > SCREEN_WIDTH) {
          // If overflows right, try to align right edge with screen margin or input right
          const diff = rect.left + POPUP_WIDTH - SCREEN_WIDTH;
          left -= diff + 30;
        }
        if (left < 0) left = 10;

        styleObject.left = left;

        // Vertical adjustment (Flip Up vs Down)
        const spaceBelow = SCREEN_HEIGHT - rect.bottom;
        const spaceAbove = rect.top;

        if (spaceBelow < POPUP_HEIGHT && spaceAbove > spaceBelow) {
          // Flip Up
          styleObject.bottom = SCREEN_HEIGHT - rect.top + 10;
          styleObject.top = 'auto'; // Clear top
        } else {
          // Default Down
          styleObject.top = rect.bottom + window.scrollY + 10; // +10 margin
          styleObject.bottom = 'auto';
        }

        setStyle(styleObject);
      }
    }
  };

  /**
   * Handles month change.
   * @param {'prev' | 'next'} direction - The direction of the month change.
   */
  const handleMonthChange = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear(viewYear - 1);
      } else {
        setViewMonth(viewMonth - 1);
      }
    } else if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear(viewYear + 1);
    } else {
      setViewMonth(viewMonth + 1);
    }
  };

  /**
   * Handles preset click.
   * @param {string} key - The key of the preset.
   */
  const handlePresetClick = (key: string) => {
    setActivePreset(key);

    if (key === 'custom') return;

    const range = calculateDateRange(key);
    if (!range) return;

    setTemporaryStartDate(range.startDate);
    setTemporaryEndDate(range.endDate);
    onChange(range.startDate, range.endDate);
    setOpen(false); // Auto close on preset

    // Update view to start date
    setViewMonth(range.startDateObj.getMonth());
    setViewYear(range.startDateObj.getFullYear());
  };

  /**
   * Handles date click.
   * @param {string} dateString - The date string.
   * @param {Date} dateObject - The date object.
   */
  const handleDateClick = (dateString: string, dateObject: Date) => {
    setActivePreset('custom');

    // If we have both, start over
    if (temporaryStartDate && temporaryEndDate) {
      setTemporaryStartDate(dateString);
      setTemporaryEndDate('');
      return;
    }

    // If we have start only
    if (temporaryStartDate && !temporaryEndDate) {
      const start = new Date(temporaryStartDate);
      if (dateObject < start) {
        setTemporaryStartDate(dateString); // New start
      } else {
        setTemporaryEndDate(dateString); // Set end
        onChange(temporaryStartDate, dateString);
        setOpen(false);
      }
      return;
    }

    // If we have nothing
    setTemporaryStartDate(dateString);
  };

  /**
   * Gets the class name for a date cell.
   * @param {Date} [dateObject] - The date object.
   * @returns {string} The class name.
   */
  const getDayClass = (dateObject: Date | undefined) => {
    if (!dateObject) return 'empty';
    dateObject.setHours(0, 0, 0, 0);
    const time = dateObject.getTime();

    let sTime = 0;
    let eventTime = 0;

    if (temporaryStartDate) {
      const d = new Date(temporaryStartDate);
      d.setHours(0, 0, 0, 0);
      sTime = d.getTime();
    }
    if (temporaryEndDate) {
      const d = new Date(temporaryEndDate);
      d.setHours(0, 0, 0, 0);
      eventTime = d.getTime();
    }

    if (sTime && eventTime) {
      if (time === sTime && time === eventTime) return 'selected start-end';
      if (time === sTime) return 'selected start';
      if (time === eventTime) return 'selected end';
      if (time > sTime && time < eventTime) return 'in-range';
    } else if (sTime) {
      if (time === sTime) return 'selected start';
      if (hoverDate) {
        const hDate = new Date(hoverDate);
        hDate.setHours(0, 0, 0, 0);
        const hTime = hDate.getTime();
        if (time > sTime && time <= hTime) return 'in-range-preview';
      }
    }

    return '';
  };

  /**
   * Handles backdrop click.
   * @param {React.MouseEvent} event - The mouse event.
   */
  const handleBackdropClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setOpen(false);
  };

  return (
    <div className="custom-input-date-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}

      <div className="custom-date-input">
        <div className="custom-input-container" onClick={toggleOpen} role="presentation">
          <div className="value-view">
            {startDate && endDate
              ? `${startDate} - ${endDate}`
              : placeholder || t('dateRangePlaceholder', { defaultValue: 'DD-MMM-YYYY - DD-MMM-YYYY' })}
          </div>
          <span className="picker-icon">
            <Calender />
          </span>
        </div>

        {open &&
          ReactDOM.createPortal(
            <div className="date-picker-portal-container">
              <div
                className="date-picker-backdrop"
                role="presentation"
                onClick={handleBackdropClick}
              />
              <div className="calender-box date-range-picker-popup" ref={reference} style={style}>
                {/* Sidebar */}
                <div className="preset-sidebar">
                  {presets.map((p) => (
                    <div
                      key={p.key}
                      role="presentation"
                      className={`preset-item ${activePreset === p.key ? 'active' : ''}`}
                      onClick={() => handlePresetClick(p.key)}>
                      {t(p.translationKey) || p.label}
                    </div>
                  ))}
                </div>

                {/* Calendars Container */}
                <div className="dual-calendar-container">
                  {/* Left Calendar */}
                  <div className="calendar-single">
                    <div className="calender-header">
                      <div
                        className="calender-header-left"
                        role="presentation"
                        onClick={() => handleMonthChange('prev')}>
                        <LeftArrow
                          className={`header-arrow-icon ${i18n.dir() === 'rtl' ? 'rtl' : ''}`}
                        />
                      </div>
                      <div className="calender-header-center">
                        {monthsList[viewMonth]} {viewYear}
                      </div>
                      <div className="calender-header-right header-arrow-hidden">
                        {/* Hidden arrow to balance layout */}
                        <RightArrow
                          className={`header-arrow-icon ${i18n.dir() === 'rtl' ? 'rtl' : ''}`}
                        />
                      </div>
                    </div>
                    <CalendarGrid
                      days={leftCalendarDays}
                      onDayClick={handleDateClick}
                      getDayClass={getDayClass}
                      onHover={setHoverDate}
                    />
                  </div>

                  {/* Right Calendar */}
                  <div className="calendar-single">
                    <div className="calender-header">
                      <div className="calender-header-left header-arrow-hidden">
                        <LeftArrow
                          className={`header-arrow-icon ${i18n.dir() === 'rtl' ? 'rtl' : ''}`}
                        />
                      </div>
                      <div className="calender-header-center">
                        {monthsList[viewMonth + 1 > 11 ? 0 : viewMonth + 1]}{' '}
                        {viewMonth + 1 > 11 ? viewYear + 1 : viewYear}
                      </div>
                      <div
                        className="calender-header-right"
                        role="presentation"
                        onClick={() => handleMonthChange('next')}>
                        <RightArrow
                          className={`header-arrow-icon ${i18n.dir() === 'rtl' ? 'rtl' : ''}`}
                        />
                      </div>
                    </div>
                    <CalendarGrid
                      days={rightCalendarDays}
                      onDayClick={handleDateClick}
                      getDayClass={getDayClass}
                      onHover={setHoverDate}
                    />
                  </div>
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

/**
 * Default props for CustomDateRangePicker component.
 */
CustomDateRangePicker.defaultProps = {
  inputLabel: '',
  valueRequired: false,
  placeholder: 'DD-MMM-YYYY - DD-MMM-YYYY',
  readOnly: false,
  defaultDuration: undefined,
  defaultPreset: undefined,
  defaultDurationSelection: undefined,
};

export default CustomDateRangePicker;
